/**
 * Everything the setup flow reports about the whole config: the issues
 * banner and stepper counts, the Review checks, and the plain-language
 * summary. Ported from the prototype.
 */
import {
  coachCapacityStatus,
  groupsRange,
  listLabel,
  oneToOneDoNotMatchCount,
  adjustedCount,
  plural,
  rangeLabel,
  resolveDoNotMatchGroups,
  unrecognizedDoNotMatchCount,
  valueCounts,
  type ValueCount,
} from './derive';
import {
  goalFeasibility,
  goalLines,
  isRanged,
  lowerLabel,
  pluralLabel,
  ruleIssue,
  ruleTitle,
  typesMax,
  typesMin,
  valueLimit,
} from './goals';
import { hasCoachData } from './defaults';
import type { SetupIssue } from './steps';
import type {
  Column,
  MatchingConfig,
  MatchingGoal,
  MatchingSessionState,
} from './types';

export type ValuesByColumn = Record<string, ValueCount[]>;

export function valuesByColumn(session: MatchingSessionState): ValuesByColumn {
  return Object.fromEntries(
    session.columns.map(c => [c.key, valueCounts(session.participants, c)])
  );
}

export function columnOf(
  session: MatchingSessionState,
  key: string
): Column | undefined {
  return session.columns.find(c => c.key === key);
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

export function collectIssues(
  session: MatchingSessionState,
  config: MatchingConfig,
  values: ValuesByColumn
): SetupIssue[] {
  const n = session.participants.length;
  const issues: SetupIssue[] = [];

  for (const goal of config.goals) {
    const column = columnOf(session, goal.column);
    const f = goalFeasibility(
      goal,
      config,
      column,
      values[goal.column] ?? [],
      n
    );
    if (!f.ok) {
      issues.push({
        blocks: true,
        text: `${column?.label ?? goal.column}: ${f.message}`,
        target: { step: 'goals', goalColumn: goal.column },
      });
    }
  }
  for (const rule of config.columnRules) {
    const column = columnOf(session, rule.column);
    const msg = ruleIssue(rule, config, column, values[rule.column] ?? [], n);
    if (msg)
      issues.push({
        blocks: true,
        text: `${ruleTitle(rule, column)}: ${msg}`,
        target: { step: 'goals' },
      });
  }
  const unsaved = config.goals.filter(g => !g.saved).length;
  if (unsaved > 0) {
    issues.push({
      blocks: true,
      text: `${unsaved} matching ${unsaved === 1 ? 'goal is' : 'goals are'} not saved yet`,
      target: { step: 'goals' },
    });
  }

  if (config.enforce.doNotMatch) {
    const unrecognized = unrecognizedDoNotMatchCount(
      session.participants,
      config
    );
    if (unrecognized > 0) {
      issues.push({
        blocks: false,
        text: `${unrecognized} one-to-one do-not-match ${unrecognized === 1 ? "email isn't" : "emails aren't"} recognized, so ${unrecognized === 1 ? 'it' : 'they'} will be skipped`,
        target: {
          step: 'data',
          dataTab: 'participants',
          onlyUnrecognizedDnm: true,
        },
      });
    }
    const missing = resolveDoNotMatchGroups(
      config.doNotMatchGroups,
      session.participants
    ).notFound.length;
    if (missing > 0) {
      issues.push({
        blocks: false,
        text: `${missing} do-not-match ${missing === 1 ? 'email was' : 'emails were'} not found, so ${missing === 1 ? 'it' : 'they'} will be skipped`,
        target: { step: 'data', dataTab: 'dnm' },
      });
    }
  }
  if (config.enforce.coachCapacity && session.coaches.length > 0) {
    const cap = coachCapacityStatus(session.coaches, session.slots, config, n);
    if (cap.level !== 'ok') {
      issues.push({
        blocks: cap.level === 'bad',
        text: cap.message,
        target: { step: 'data', dataTab: 'coaches' },
      });
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Review: checks
// ---------------------------------------------------------------------------

export interface ReviewCheck {
  ok: boolean;
  text: string;
  /** Present when the check has a Fix link. */
  issue?: SetupIssue;
}

export function reviewChecks(
  session: MatchingSessionState,
  config: MatchingConfig,
  issues: SetupIssue[]
): ReviewCheck[] {
  const checks: ReviewCheck[] = [];
  const goalIssues = issues.filter(
    i => i.target.step === 'goals' && i.target.goalColumn
  );
  if (goalIssues.length === 0) {
    checks.push({
      ok: true,
      text:
        config.goals.length === 0
          ? 'No matching goals set'
          : 'All matching goals look possible',
    });
  }
  for (const i of issues.filter(x => x.target.step === 'goals'))
    checks.push({ ok: false, text: i.text, issue: i });

  if (!config.enforce.doNotMatch)
    checks.push({ ok: true, text: 'Do-not-matches are not enforced' });
  else {
    const dnm = issues.filter(
      i => i.target.dataTab === 'dnm' || i.target.onlyUnrecognizedDnm
    );
    if (dnm.length === 0)
      checks.push({ ok: true, text: 'All do-not-match emails matched' });
    for (const i of dnm) checks.push({ ok: false, text: i.text, issue: i });
  }
  if (!hasCoachData(session))
    checks.push({
      ok: true,
      text: 'No coach data, so coach capacity is not enforced',
    });
  else if (!config.enforce.coachCapacity)
    checks.push({ ok: true, text: 'Coach capacity is not enforced' });
  else {
    const cap = issues.find(i => i.target.dataTab === 'coaches');
    checks.push(
      cap
        ? { ok: false, text: cap.text, issue: cap }
        : { ok: true, text: 'Coach capacity covers every group and every time' }
    );
  }
  if (!config.goals.some(g => !g.saved))
    checks.push({ ok: true, text: 'All matching goals are set up' });
  return checks;
}

// ---------------------------------------------------------------------------
// Review: plain-language summary
// ---------------------------------------------------------------------------

export interface RequirementLine {
  enforced: boolean;
  name: string;
  text: string;
}

export function requirementLines(
  session: MatchingSessionState,
  config: MatchingConfig
): RequirementLine[] {
  const n = session.participants.length;
  const groups = groupsRange(n, config.groupSize);
  const dnm = resolveDoNotMatchGroups(
    config.doNotMatchGroups,
    session.participants
  );
  const adjusted = adjustedCount(session.participants, config);
  const cap = coachCapacityStatus(session.coaches, session.slots, config, n);
  const lines: RequirementLine[] = [
    {
      enforced: true,
      name: 'group size',
      text: `Group size: ${rangeLabel(config.groupSize.min, config.groupSize.max)} people, which gives ${groups.label} groups.`,
    },
    {
      enforced: config.enforce.doNotMatch,
      name: 'do-not-matches',
      text: `Do-not-matches: ${dnm.totalEmails} emails in lists, ${oneToOneDoNotMatchCount(session.participants, config)} one-to-one.`,
    },
    {
      enforced: config.enforce.availability,
      name: 'participant availability',
      text: `Participant availability: ${adjusted > 0 ? `${plural(adjusted, 'availability change')} adjusted.` : 'no changes made.'}`,
    },
    hasCoachData(session)
      ? {
          enforced: config.enforce.coachCapacity,
          name: 'coach capacity',
          text: `Coach capacity: coaches cover ${cap.total} groups${cap.level === 'ok' ? `, enough for the ${groups.label} needed.` : `, which may not be enough for the ${groups.label} needed.`}`,
        }
      : {
          enforced: false,
          name: 'coach capacity',
          text: 'Coach capacity: this matching session has no coach data.',
        },
  ];
  for (const rule of config.columnRules) {
    const column = columnOf(session, rule.column);
    const label = lowerLabel(column);
    lines.push({
      enforced: rule.enabled,
      name:
        rule.type === 'keep_together'
          ? `keeping ${label} together`
          : `no ${label} overlap`,
      text: `${column?.label ?? rule.column}: ${rule.type === 'keep_together' ? 'keep together' : 'no overlap'}`,
    });
  }
  return lines;
}

export function requirementSummary(lines: RequirementLine[]): string {
  const on = lines.filter(l => l.enforced).map(l => l.name);
  const off = lines.filter(l => !l.enforced).map(l => l.name);
  return `Enforced: ${listLabel(on)}.${off.length > 0 ? ` Not enforced: ${listLabel(off)}.` : ''} See details.`;
}

function goalIntent(
  goal: MatchingGoal,
  column: Column | undefined,
  values: ValueCount[]
): string {
  const label = lowerLabel(column);
  const flag = goal.saved ? '' : ' (not saved)';
  if (goal.how === 'keep_together') return `Keep ${label} together${flag}`;
  const n = values.length;
  const restricted =
    typesMin(goal, n) > 2 ||
    typesMax(goal, n) < n ||
    goal.avoid.length > 0 ||
    values.some(v => {
      const r = valueLimit(goal, v.value);
      return r.every || isRanged(r);
    });
  return `${goal.buddy.enabled ? 'Buddy mix of ' : 'Spread out '}${pluralLabel(label)}${restricted ? '; specific restrictions on the mix' : ''}${flag}`;
}

export function goalSummary(
  session: MatchingSessionState,
  config: MatchingConfig,
  values: ValuesByColumn
): string {
  if (config.goals.length === 0) return 'No matching goals set.';
  return `${config.goals.map(g => goalIntent(g, columnOf(session, g.column), values[g.column] ?? [])).join('. ')}. See details.`;
}

export function introText(
  session: MatchingSessionState,
  config: MatchingConfig
): string {
  const n = session.participants.length;
  const groups = groupsRange(n, config.groupSize);
  return `Matching will split the ${n} participants into ${groups.label} groups of ${rangeLabel(config.groupSize.min, config.groupSize.max)} people. You will see matchings that follow every group requirement below and meet one or more of your matching goals.`;
}

export interface GoalDetail {
  column: string;
  name: string;
  saved: boolean;
  lines: string[];
}

export function goalDetails(
  session: MatchingSessionState,
  config: MatchingConfig,
  values: ValuesByColumn
): GoalDetail[] {
  return config.goals.map(g => {
    const column = columnOf(session, g.column);
    return {
      column: g.column,
      name: column?.label ?? g.column,
      saved: g.saved,
      lines: goalLines(g, column, values[g.column] ?? []),
    };
  });
}
